// Per-trip color palette — the Editorial color engine ported from
// design_handoff_web/dcg-engine.jsx (#246 / #177). A key's color is GENERATED,
// not picked from a table — but generated INSIDE a muted "Editorial" band so it
// can never come out neon, and de-collided per trip so neighbouring legs stay
// distinguishable. Same generator feeds the rail, day-card tint, and hero
// gradient, so the whole trip reads as one coherent palette. Keep in sync with
// mobile's city-colors.ts when that adopts the band.
//
// paletteForDestIds keeps its signature (consumers pass destId slugs) — the
// key is hashed deterministically, so a given destId is always the same hue
// (independent of trip), then de-collided within the trip.

export interface TripSwatch {
  c1: string; // surface stop (mid lightness)
  c2: string; // deeper gradient stop (same hue, ~19% darker)
  h?: number;
}

// FNV-1a — same string → same number forever.
function hash(str: string): number {
  let h = 2166136261;
  const s = (str || '').toLowerCase().trim();
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ~1 around 40° (amber/gold), ~0 around 220° (slate/indigo).
function warmth(h: number): number {
  return (Math.cos(((h - 40) * Math.PI) / 180) + 1) / 2;
}

// Damp saturation around 115° (green) so those hues drift toward sage.
function greenDamp(h: number): number {
  const d = h - 115;
  return 1 - 0.20 * Math.exp(-(d * d) / (2 * 42 * 42));
}

function baseHue(key: string): number {
  return hash(key) % 360;
}

function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360;
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const to = (x: number) =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, '0');
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`;
}

export function editorialFromHue(h: number): TripSwatch {
  const w = warmth(h);
  const gd = greenDamp(h);
  const s1 = (34 + w * 12) * gd; // ~33–46% — muted-magazine but chromatic; cool hues keep enough sat to separate
  const l1 = 56 + w * 5; // 56–61%
  const s2 = Math.min(s1 + 10, 54);
  const l2 = l1 - 19;
  return {
    h: Math.round(((h % 360) + 360) % 360),
    c1: hslToHex(h, s1, l1),
    c2: hslToHex(h, s2, l2),
  };
}

const MIN_SEP = 34; // degrees — minimum hue separation between trip legs (de-collided per-trip, so a wide gap is affordable)

export function paletteForDestIds(
  destIds: Array<string | null | undefined>,
): Record<string, TripSwatch> {
  const uniq: string[] = [];
  const seen = new Set<string>();
  for (const id of destIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    uniq.push(id);
  }
  const hues = uniq.map(baseHue);

  // iterative repulsion on the hue circle — keeps stable cities put, fans
  // apart only genuine collisions. Deterministic, converges fast.
  for (let pass = 0; pass < 24; pass++) {
    let moved = false;
    for (let i = 0; i < hues.length; i++) {
      for (let j = i + 1; j < hues.length; j++) {
        const d = ((hues[j] - hues[i] + 540) % 360) - 180;
        const ad = Math.abs(d);
        if (ad < MIN_SEP) {
          const push = (MIN_SEP - ad) / 2 + 0.5;
          const dir = d === 0 ? 1 : Math.sign(d);
          hues[i] = (hues[i] - dir * push + 360) % 360;
          hues[j] = (hues[j] + dir * push + 360) % 360;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }

  const out: Record<string, TripSwatch> = {};
  uniq.forEach((id, i) => {
    out[id] = editorialFromHue(hues[i]);
  });
  return out;
}

// Sticky incremental assignment — for palettes that must PERSIST (e.g. the
// planner's per-trip city colors across PLAN + comparison rows). A new key
// joins an already-assigned set WITHOUT moving anyone: start at the key's
// base hue; if that collides (< MIN_SEP) with a taken hue, take the nearest
// free offset; if the circle is saturated, the midpoint of the largest gap.
export function stickyHueForKey(key: string, taken: number[]): number {
  const norm = (h: number) => ((h % 360) + 360) % 360;
  const dist = (a: number, b: number) => Math.abs(((a - b + 540) % 360) - 180);
  const free = (h: number) => taken.every((t) => dist(h, t) >= MIN_SEP);
  const base = norm(baseHue(key));
  if (taken.length === 0 || free(base)) return base;
  for (let off = 1; off <= 180; off++) {
    if (free(norm(base + off))) return norm(base + off);
    if (free(norm(base - off))) return norm(base - off);
  }
  const sorted = taken.map(norm).sort((a, b) => a - b);
  let bestMid = base;
  let bestGap = -1;
  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i]!;
    const b = i + 1 < sorted.length ? sorted[i + 1]! : sorted[0]! + 360;
    const gap = b - a;
    if (gap > bestGap) {
      bestGap = gap;
      bestMid = norm(a + gap / 2);
    }
  }
  return Math.round(bestMid);
}

// Single-key color (stable, not de-collided) — for chips/swatches outside a
// trip context.
export function swatchForKey(key: string | null | undefined): TripSwatch {
  if (!key) return { h: 36, c1: hslToHex(36, 10, 56), c2: hslToHex(36, 12, 40) };
  return editorialFromHue(baseHue(key));
}

// Faint tint of a hex over the cream surface (#FCFAF6), for day-card washes.
export function cityTint(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  const b = { r: 252, g: 250, b: 246 };
  const m = (k: 'r' | 'g' | 'b') => Math.round(b[k] + (c[k] - b[k]) * amt);
  return `rgb(${m('r')}, ${m('g')}, ${m('b')})`;
}
